window.EDDService = (function () {
  const API_BASE_URL = "https://api.thedermaco.com/v1/order";
  const STORAGE_KEY = "edd_delivery_data";

  async function fetchDeliveryEstimate(pincode, sku) {
    if (!pincode || pincode.length !== 6) {
      throw new Error(
        "Invalid pincode. Please provide a valid 6-digit pincode."
      );
    }

    try {
      const baseUrl = API_BASE_URL.replace(/\/+$/, "");
      const response = await fetch(
        sku ? `${baseUrl}/estimate-delivery-date/${pincode}?sku=${sku}` :  `${baseUrl}/estimate-delivery-date/${pincode}`
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();

      if (data.status === "ok" && data.data) {
        const estimatedDate = data.data.estimated_date;
        const etd = data.data.recommended_courier?.etd || estimatedDate;

        return {
          pincode: pincode,
          deliveryDate: etd,
          estimatedDate: estimatedDate,
          fullResponse: data,
          timestamp: new Date().getTime(),
        };
      } else {
        throw new Error("Unable to get delivery estimate. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching delivery estimate:", error);
      throw error;
    }
  }

  /**
   * Save delivery data to session storage
   */
  function saveDeliveryData(deliveryData) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(deliveryData));

      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new CustomEvent("edd:data-saved", {
          detail: deliveryData,
        })
      );
    } catch (error) {
      console.error("Error saving delivery data:", error);
    }
  }

  /**
   * Load delivery data from session storage
   */
  function loadDeliveryData() {
    try {
      const savedData = sessionStorage.getItem(STORAGE_KEY);

      if (savedData) {
        const data = JSON.parse(savedData);

        // Validate data structure
        if (data.pincode && data.deliveryDate) {
          return data;
        }
      }

      return null;
    } catch (error) {
      console.error("Error loading delivery data:", error);
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  /**
   * Clear saved delivery data
   */
  function clearDeliveryData() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);

      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent("edd:data-cleared"));
    } catch (error) {
      console.error("Error clearing delivery data:", error);
    }
  }

  /**
   * Get customer pincode from customer data
   */
  function getCustomerPincode(customerPincode) {
    if (customerPincode && customerPincode.length === 6) {
      return customerPincode;
    }
    return null;
  }

  /**
   * Get delivery data with priority: customer pincode > saved data
   */
  async function getDeliveryData(customerPincode) {
    // First priority: customer pincode
    const validCustomerPincode = getCustomerPincode(customerPincode);

    if (validCustomerPincode) {
      try {
        const deliveryData = await fetchDeliveryEstimate(validCustomerPincode);
        saveDeliveryData(deliveryData);
        return deliveryData;
      } catch (error) {
        console.error(
          "Error fetching delivery data for customer pincode:",
          error
        );
      }
    }

    // Second priority: saved data
    const savedData = loadDeliveryData();
    if (savedData) {
      return savedData;
    }

    return null;
  }

  /**
   * Format delivery date for display
   */
  function formatDeliveryDate(deliveryDate) {
    try {
      // If it's already a formatted string, return as is
      if (typeof deliveryDate === "string") {
        return deliveryDate;
      }

      const date = new Date(deliveryDate);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return deliveryDate;
    }
  }

  // Public API
  return {
    fetchDeliveryEstimate,
    saveDeliveryData,
    loadDeliveryData,
    clearDeliveryData,
    getCustomerPincode,
    getDeliveryData,
    formatDeliveryDate,

    // Constants
    STORAGE_KEY,
  };
})();
